import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import App from './App.tsx';
import './index.css';
import { ErrorBoundary } from './components/ErrorBoundary';

// Global API URL rewrite helper: prefer runtime `window.__BACKEND_URL__`,
// fall back to build-time `VITE_BACKEND_URL` or `APP_URL`.
const runtimeBackend = (window as any).__BACKEND_URL__?.trim();
const buildBackend =
  import.meta.env.VITE_BACKEND_URL?.trim() || import.meta.env.APP_URL?.trim() || '';
const backendUrl = runtimeBackend || buildBackend || '';

const canonicalHost = 'pambo.biz';
const currentHost = window.location.hostname;
if (currentHost === `www.${canonicalHost}`) {
  const target = `https://${canonicalHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(target);
}

// ── Production: suppress all console output ─────────────────────────────────
// Logs are a security risk in production (they can leak user data, internal
// paths, and API details visible in DevTools). Suppress everything except
// console.error so that real crashes still reach Sentry.
if (import.meta.env.PROD) {
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.warn = noop;
  console.debug = noop;
  console.trace = noop;
  console.group = noop;
  console.groupEnd = noop;
  console.groupCollapsed = noop;
}
// ─────────────────────────────────────────────────────────────────────────────

if (backendUrl) {
  // Configure default baseURL for all Axios requests
  axios.defaults.baseURL = backendUrl;

  // Intercept all global window.fetch requests to relative '/api/' paths
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    let finalInput = input;
    if (typeof input === 'string') {
      if (input.startsWith('/api/')) {
        finalInput = `${backendUrl}${input}`;
      }
    } else if (input instanceof URL) {
      if (input.pathname.startsWith('/api/')) {
        finalInput = new URL(`${backendUrl}${input.pathname}${input.search}`);
      }
    } else if (input instanceof Request) {
      if (input.url.startsWith('/') && !input.url.startsWith('//')) {
        if (input.url.startsWith('/api/')) {
          finalInput = new Request(`${backendUrl}${input.url}`, input);
        }
      } else {
        try {
          const urlObj = new URL(input.url);
          if (urlObj.pathname.startsWith('/api/') && urlObj.origin === window.location.origin) {
            finalInput = new Request(`${backendUrl}${urlObj.pathname}${urlObj.search}`, input);
          }
        } catch {
          // Fallback if URL parsing fails
        }
      }
    }
    return originalFetch(finalInput, init);
  };
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: (import.meta.env as any).VITE_SENTRY_RELEASE || undefined,
    environment: (import.meta.env as any).MODE || 'development',
    sendDefaultPii: false,
    beforeSend(event) {
      try {
        if (event.user) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          delete event.user.email;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          delete event.user.ip_address;
        }
      } catch {
        // ignore
      }
      return event;
    },
    integrations: [Sentry.browserTracingIntegration()],
    // Performance Monitoring
    tracesSampleRate: (import.meta.env as any).MODE === 'production' ? 0.05 : 0,
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);
